import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { updateWhatsapp } from "../../services/whatsapp.api";
import {
  getTelegramConfig,
  connectTelegramBot,
  getTelegramBot,
  replaceTelegramToken,
} from "../../services/telegram.api";

/**
 * Modal del canal Telegram (doc 0438 TG6). Mismo patrón que WhatsAppFacebookModal: modal
 * dedicado, sin QR. Alta = nombre + token de @BotFather; el backend valida el token con
 * Telegram, crea la conexión y registra la entrada (webhook o polling). Edición = renombrar
 * y, si hace falta, reemplazar el token. El token nunca vuelve del servidor.
 */

const TOKEN_FORMAT = /^\d{5,}:[A-Za-z0-9_-]{30,}$/;

const TelegramConnectionModal = ({ open, onClose, whatsAppId, onSave, onOpenAdvanced }) => {
  const [name, setName] = useState("");
  const [botToken, setBotToken] = useState("");
  const [bot, setBot] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open) return;
      setLoading(true);
      try {
        const { data: cfg } = await getTelegramConfig();
        if (!mounted) return;
        setConfig(cfg);
        if (whatsAppId && cfg?.enabled) {
          const { data } = await getTelegramBot(whatsAppId);
          if (!mounted) return;
          setBot(data);
          setName(data.connectionName || "");
        }
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, whatsAppId]);

  const handleClose = useCallback(() => {
    setName("");
    setBotToken("");
    setBot(null);
    setSaving(false);
    onClose();
  }, [onClose]);

  const tokenLimpio = botToken.trim();
  const tokenValido = TOKEN_FORMAT.test(tokenLimpio);

  const handleSave = async () => {
    if (!whatsAppId && !tokenValido) {
      toast.error("Pega el token completo que te dio @BotFather (formato 123456789:AA…).");
      return;
    }
    if (whatsAppId && tokenLimpio && !tokenValido) {
      toast.error("El token nuevo no tiene el formato de @BotFather (123456789:AA…).");
      return;
    }
    setSaving(true);
    try {
      if (whatsAppId) {
        if (name.trim() && name.trim() !== bot?.connectionName) {
          await updateWhatsapp(whatsAppId, { name: name.trim() });
        }
        if (tokenLimpio) await replaceTelegramToken(whatsAppId, tokenLimpio);
        toast.success("Conexión de Telegram actualizada.");
      } else {
        const { data } = await connectTelegramBot({ name: name.trim(), botToken: tokenLimpio });
        toast.success(
          data?.mode === "polling"
            ? `Bot @${data.botUsername} conectado (modo desarrollo: polling). Escríbele desde Telegram para probar.`
            : `Bot @${data.botUsername} conectado. Escríbele desde Telegram para probar.`
        );
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  const deshabilitado = config && !config.enabled;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{whatsAppId ? "Editar conexión de Telegram" : "Conectar un bot de Telegram"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : deshabilitado ? (
          <Typography variant="body2">
            El canal Telegram no está habilitado en esta plataforma. El administrador del servidor
            debe activar <code>TELEGRAM_CHANNEL_ENABLED=true</code> en el backend.
          </Typography>
        ) : (
          <>
            {!whatsAppId && (
              <Box mb={2}>
                <Typography variant="body2" gutterBottom>
                  1. En Telegram, abre <b>@BotFather</b> y envía <code>/newbot</code> (o usa uno existente con <code>/mybots</code>).
                </Typography>
                <Typography variant="body2" gutterBottom>
                  2. Copia el <b>token</b> que te entrega y pégalo aquí.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Los clientes te escriben buscando el @usuario del bot. Un bot no puede iniciar
                  conversaciones: solo responde a quien le escribió primero.
                </Typography>
              </Box>
            )}

            {whatsAppId && bot && (
              <Box mb={2}>
                <Typography variant="body2">
                  Bot: <b>@{bot.botUsername}</b> · Entrada: {bot.mode === "polling" ? "polling (desarrollo)" : "webhook"}
                </Typography>
                {bot.lastErrorCode ? (
                  <Typography variant="body2" style={{ color: "#c62828" }}>
                    Último error de Telegram ({bot.lastErrorCode}): {bot.lastErrorDescription}
                  </Typography>
                ) : null}
              </Box>
            )}

            <TextField
              label="Nombre de la conexión"
              helperText={whatsAppId ? "" : "Opcional: si lo dejas vacío se usa «Telegram @usuario_del_bot»"}
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label={whatsAppId ? "Token nuevo (solo si quieres reemplazarlo)" : "Token del bot"}
              placeholder="123456789:AA…"
              variant="outlined"
              margin="dense"
              fullWidth
              type="password"
              autoComplete="off"
              required={!whatsAppId}
              error={Boolean(tokenLimpio) && !tokenValido}
              helperText={tokenLimpio && !tokenValido ? "Formato esperado: números, dos puntos y el código de @BotFather" : "Se guarda cifrado y nunca se vuelve a mostrar"}
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {whatsAppId && onOpenAdvanced && !deshabilitado && (
          <Button onClick={onOpenAdvanced} disabled={saving || loading} style={{ marginRight: "auto" }}>
            Colas, flujo e IA…
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || deshabilitado || (!whatsAppId && !tokenValido)}
        >
          {saving ? <CircularProgress size={20} /> : whatsAppId ? "Guardar" : "Conectar bot"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TelegramConnectionModal;
